import * as React from 'react';
import {
    Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    Button, TablePagination, TableRow, TextField, Grid2, IconButton, Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PrintIcon from '@mui/icons-material/Print';
import { useTheme } from '@emotion/react';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import SwtichButton from './SwitchButton';
import dayjs from 'dayjs';

export default function StickyHeadTable(props) {
    const modernStyle = props.modernStyle === true;
    const theme = useTheme();

    const perPage = props.perPage;
    const tableColumns = props.tableColumns;
    const rows = props.tableData;
    const hasExplicitActions = tableColumns.some((column) => column.action === true);
    const dataColumns = hasExplicitActions
        ? tableColumns.slice(1).filter((column) => column.action !== true)
        : tableColumns.slice(1, -3);
    const actionColumns = hasExplicitActions
        ? tableColumns.slice(1).filter((column) => column.action === true)
        : tableColumns.slice(-3);

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(perPage || 5);
    const [filter, setFilter] = React.useState('');
    const [filteredRows, setFilteredRows] = React.useState(rows);
    const [savedColumnWidths, setSavedColumnWidths] = React.useState({});
    const isResizingRef = React.useRef(false);

    // Sorting states
    const [orderBy, setOrderBy] = React.useState('');
    const [order, setOrder] = React.useState('asc');

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleFilterChange = (event) => {
        const filterValue = event.target.value.toLowerCase();
        setFilter(filterValue);

        const filteredData = rows.filter(row => {
            return tableColumns.some(col => {
                const cellValue = row[col.fieldName]?.toString().toLowerCase();
                return cellValue && cellValue.includes(filterValue);
            });
        });

        setFilteredRows(filteredData);
    };

    const handleSort = (column) => {
        const isAsc = orderBy === column && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(column);
    };

    const sortedRows = React.useMemo(() => {
        if (!orderBy) return filteredRows;
        return [...filteredRows].sort((a, b) => {
            const aVal = a[orderBy];
            const bVal = b[orderBy];

            if (aVal === undefined || bVal === undefined) return 0;

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return order === 'asc' ? aVal - bVal : bVal - aVal;
            } else {
                return order === 'asc'
                    ? aVal.toString().localeCompare(bVal.toString())
                    : bVal.toString().localeCompare(aVal.toString());
            }
        });
    }, [filteredRows, orderBy, order]);

    React.useEffect(() => {
        setFilteredRows(rows);
        setPage(0);
    }, [rows]);

    const getColumnWidth = (column) => savedColumnWidths[column.fieldName] || column.width;

    const startColumnResize = (event, column) => {
        event.preventDefault();
        event.stopPropagation();
        const startX = event.clientX;
        const startWidth = event.currentTarget.parentElement.getBoundingClientRect().width;
        const minWidth = Number(column.minWidth) || (column.action ? 72 : 90);
        isResizingRef.current = true;
        const resizeColumn = (moveEvent) => {
            setSavedColumnWidths((widths) => ({
                ...widths,
                [column.fieldName]: Math.max(minWidth, Math.round(startWidth + moveEvent.clientX - startX))
            }));
        };
        const stopResize = () => {
            window.removeEventListener('mousemove', resizeColumn);
            window.removeEventListener('mouseup', stopResize);
            window.setTimeout(() => { isResizingRef.current = false; }, 0);
        };
        window.addEventListener('mousemove', resizeColumn);
        window.addEventListener('mouseup', stopResize);
    };

    return (
        <Paper elevation={0} sx={{ width: '100%', overflow: 'hidden', backgroundColor: modernStyle ? 'transparent' : 'rgb(250, 250, 250)', borderRadius: modernStyle ? 2.5 : 0, border: modernStyle ? '1px solid rgba(15, 23, 42, 0.08)' : 'none' }}>
            <Grid2 container spacing={1} justifyContent={modernStyle ? "flex-start" : "center"} alignItems="center" sx={modernStyle ? { p: 2, bgcolor: "rgba(248, 250, 252, 0.82)" } : undefined}>
                <Grid2 size={modernStyle ? { xs: 12, sm: 7, md: 5 } : 4}>
                    <TextField
                        sx={modernStyle ? { width: '100%', '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } } : { margin: '20px', width: '100%' }}
                        label={modernStyle ? 'Search records' : 'Filter'}
                        variant="outlined"
                        fullWidth
                        value={filter}
                        onChange={handleFilterChange}
                        placeholder="Search by any column..."
                        size="small"
                    />
                </Grid2>
            </Grid2>

            <TableContainer sx={{ overflowX: 'auto', ...(modernStyle ? { borderTop: '1px solid rgba(15, 23, 42, 0.08)' } : {}) }}>
                <Table stickyHeader aria-label="sticky table" size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            {dataColumns.map((col, index) => (
                                col.status === 'en' && (
                                    <TableCell
                                        key={index}
                                        style={{
                                            backgroundColor: modernStyle ? 'var(--web-theme-primary, #2563eb)' : theme.palette.primary.main,
                                            color: modernStyle ? 'var(--web-theme-button-text, #fff)' : 'white',
                                            textAlign: col.textAlign,
                                            fontWeight: 'bolder',
                                            width: getColumnWidth(col),
                                            minWidth: col.minWidth || 90,
                                            textShadow: modernStyle ? 'none' : 'black 2px 2px 4px',
                                            padding: modernStyle ? '13px 16px' : undefined,
                                            borderBottom: modernStyle ? 'none' : undefined,
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => { if (!isResizingRef.current) handleSort(col.fieldName); }}
                                        sortDirection={orderBy === col.fieldName ? order : false}
                                    >
                                        {/* <TableSortLabel
                                            active={orderBy === col.fieldName}
                                            direction={orderBy === col.fieldName ? order : 'asc'}
                                            onClick={() => handleSort(col.fieldName)}
                                            style={{ color: 'white' }}
                                        >
                                            {col.cName}
                                        </TableSortLabel> */}
                                        {col.cName}
                                        <span role='separator' aria-label={`Resize ${col.cName} column`} onMouseDown={(event) => startColumnResize(event, col)} style={{ position: 'absolute', top: 0, right: -4, zIndex: 2, width: 8, height: '100%', cursor: 'col-resize' }} />
                                    </TableCell>
                                )
                            ))}
                            {actionColumns.map((col, i) =>
                                col.status === 'en' ? (
                                    <TableCell
                                        key={`action-${i}`}
                                        style={{
                                            backgroundColor: modernStyle ? 'var(--web-theme-primary, #2563eb)' : theme.palette.primary.main,
                                            color: modernStyle ? 'var(--web-theme-button-text, #fff)' : 'white',
                                            textAlign: col.textAlign,
                                            fontWeight: 'bolder',
                                            width: getColumnWidth(col),
                                            maxWidth: getColumnWidth(col),
                                            minWidth: col.minWidth || 72,
                                            whiteSpace: 'nowrap',
                                            textShadow: modernStyle ? 'none' : 'black 2px 2px 4px',
                                            padding: modernStyle ? '13px 16px' : undefined,
                                            borderBottom: modernStyle ? 'none' : undefined,
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {col.cName}
                                        <span role='separator' aria-label={`Resize ${col.cName} column`} onMouseDown={(event) => startColumnResize(event, col)} style={{ position: 'absolute', top: 0, right: -4, zIndex: 2, width: 8, height: '100%', cursor: 'col-resize' }} />
                                    </TableCell>
                                ) : null
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedRows
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row, rowIndex) => (
                                <TableRow key={rowIndex} sx={modernStyle ? { bgcolor: rowIndex % 2 === 0 ? '#fff' : 'rgba(248, 250, 252, 0.7)', transition: 'background-color 160ms ease', '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.06)' } } : undefined}>
                                    {dataColumns.map((col, index) => (
                                        <TableCell
                                            key={index}
                                            style={{
                                                textAlign: col.textAlign,
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                border: modernStyle ? 'none' : 'ridge 1px',
                                                borderBottom: modernStyle ? '1px solid rgba(15, 23, 42, 0.08)' : undefined,
                                                padding: modernStyle ? '13px 16px' : undefined
                                            }}>
                                            {col.dType === 'currency'
                                                ? `$${Math.round(row[col.fieldName]).toLocaleString("en-US")}`
                                                : col.dType === 'number'
                                                    ? `${Math.round(row[col.fieldName]).toLocaleString("en-US")}`
                                                    : col.swithButton ? (
                                                        <SwtichButton
                                                            status={row[col.fieldName]}
                                                            funcOnChange={props.onChangeSwitch}
                                                        />
                                                    ) : col.dType === 'date' ? (
                                                        dayjs(row[col.fieldName]).format('DD-MM-YYYY')
                                                    ) : col.dType === 'decimal' ? (
                                                        `$${(Math.round(row[col.fieldName] * 1000) / 1000).toLocaleString("en-US")}`
                                                    ) : (
                                                        row[col.fieldName]
                                                    )}
                                        </TableCell>
                                    ))}
                                    {/* Action buttons */}
                                    {actionColumns.map((col, actionIndex) => col.status === 'en' && (
                                        <TableCell key={`row-action-${actionIndex}`} style={{ textAlign: 'center', border: modernStyle ? 'none' : 'ridge 1px', borderBottom: modernStyle ? '1px solid rgba(15, 23, 42, 0.08)' : undefined }}>
                                            {col.icon === 'print' ? <Tooltip title="Print">
                                                <IconButton color="primary" size="small" onClick={() => (props.onPrintClick || props.onButtonClick)(row[tableColumns[0].fieldName])}>
                                                    <PrintIcon />
                                                </IconButton>
                                            </Tooltip> : col.icon === 'excel' ? <Tooltip title="Export Excel">
                                                <IconButton color="success" size="small" onClick={() => props.onExcelClick?.(row[tableColumns[0].fieldName])}>
                                                    <TableViewIcon />
                                                </IconButton>
                                            </Tooltip> : col.icon === 'pdf' ? <Tooltip title="Export PDF">
                                                <IconButton color="error" size="small" onClick={() => props.onPdfClick?.(row[tableColumns[0].fieldName])}>
                                                    <PictureAsPdfIcon />
                                                </IconButton>
                                            </Tooltip> : col.fieldName === 'Edit' ? <EditIcon
                                                sx={{
                                                    cursor: 'pointer',
                                                    // color: theme.palette.primary.main,
                                                    // border: `1px solid ${theme.palette.primary.main}`,
                                                    // borderRadius: 1,
                                                    // p: 0.5,
                                                    // boxShadow: '1px 1px 1px 0px grey'
                                                }}
                                                onClick={() => {
                                                    props.onEditClick(row[tableColumns[0].fieldName]);
                                                }}
                                            /> : col.fieldName === 'Delete' ? <DeleteForeverIcon
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => props.onDeleteClick(row[tableColumns[0].fieldName])}
                                            /> : <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => props.onButtonClick(row[tableColumns[0].fieldName])}
                                                disabled={Boolean(col.hideWhenField && row[col.hideWhenField] === col.hideWhenValue)}
                                                sx={{ minWidth: 84, px: 1.5, py: 0.4, fontSize: 12, lineHeight: 1.4 }}
                                            >
                                                {col.buttonText}
                                            </Button>}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                sx={modernStyle ? { bgcolor: 'rgba(248, 250, 252, 0.82)', borderTop: '1px solid rgba(15, 23, 42, 0.08)' } : undefined}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={sortedRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
        </Paper>
    );
}
